<template>
  <ui-modal
    v-model="isVisible"
    style-classes="bg-black bg-noise rounded-2xl shadow-lg"
  >
    <div class="px-8">
      <div
        class="
          my-10
          text-4xl
          font-semibold
          leading-tight
          text-center
          text-white
        "
      >
        Get your Compass
        <div class="inline-block relative text-indigo-500">
          Premium Access<img
            src="../assets/underline.svg"
            class="absolute top-full left-0 w-full h-auto"
          >
        </div>
      </div>

      <div v-if="showBillingInfoForm">
        <div class="pb-2 text-white">
          Selected Plan
        </div>
        <div
          class="
            flex
            justify-between
            items-center
            py-4
            px-4
            mb-3
            text-gray-100
            rounded-lg
            hover:opacity-100
            cursor-pointer
            bg-gray-900 opacity-90
          "
        >
          <div class="font-medium">
            {{ plans.find(item => item.id === selectedPlanId).displayName }}
          </div>
          <div class="font-mono">
            {{ formatCurrency(selectedPlanPrice) }}
          </div>
        </div>

        <div class="mb-2 text-white">
          Billing Info
        </div>
        <billing-info-form
          v-model:name="billingInfoModel.name"
          v-model:address="billingInfoModel.address"
          v-model:country="billingInfoModel.country"
          v-model:vat-number="billingInfoModel.vatNumber"
          v-model:is-valid="billingInfoModelValid"
          @inputEnter="fillBillingInfo"
        />

        <div v-if="fillingBillingInfo">
          <view-loader size="sm" />
        </div>
        <div v-else>
          <ui-button
            variant="primary"
            block
            size="xl"
            class="mb-2"
            :disabled="selectedPlanId === null || isCheckingOut || !billingInfoModelValid"
            @click="fillBillingInfo"
          >
            Continue Purchase
          </ui-button>
          <div
            class="block py-3 mt-2 w-full text-center text-gray-100 opacity-75 cursor-pointer"
            @click="showBillingInfoForm = false"
          >
            Back
          </div>
        </div>
      </div>

      <div v-else-if="showPurchaseOverview">
        <div class="flex justify-center pb-2 text-lg text-white">
          Purchase Overview
        </div>

        <view-loader
          v-if="applyCouponLoading"
          size="sm"
        />
        <ui-card
          v-if="appliedCoupon && appliedCouponDiscount"
          class="py-3 px-2 mb-2"
        >
          <div class="pb-2 text-sm text-white">
            <span class="text-gray-400">Applied Coupon</span> : <span class="font-bold">{{ appliedCoupon }}</span>
          </div>

          <div class="pb-2 text-sm text-white">
            <span class="text-gray-400">Discount</span> : <span class="font-bold">{{ appliedCouponDiscount * 100 }}%</span>
          </div>

          <div class="pb-2 text-sm text-white">
            <span class="text-gray-400">Old Price</span> : <span class="font-bold">{{ formatCurrency(selectedPlanPrice) }}</span>
          </div>

          <div class="mt-2 text-sm font-bold text-gray-400">
            <span
              class="cursor-pointer"
              @click="removeAppliedCoupon"
            >
              Remove
            </span>
          </div>
        </ui-card>

        <div class="pb-2 text-white">
          Selected Plan
        </div>

        <div
          class="
            flex
            justify-between
            items-center
            py-4
            px-4
            mb-3
            text-gray-100
            rounded-lg
            hover:opacity-100
            cursor-pointer
            bg-gray-900 opacity-90
          "
        >
          <div class="font-medium">
            {{ plans.find(item => item.id === selectedPlanId).displayName }}
          </div>
          <div :class="['font-mono', appliedCouponDiscount ? 'text-indigo-500' : '']">
            {{ formatCurrency(planPriceWithDiscount(selectedPlanPrice)) }}
          </div>
        </div>

        <ui-input
          v-model="couponCode"
          placeholder="Coupon"
          @keyup.enter="applyCoupon"
        />

        <ui-button
          block
          variant="white"
          class="mt-2"
          :disabled="!couponCode"
          @click="applyCoupon"
        >
          Apply coupon
        </ui-button>

        <ui-button
          variant="primary"
          block
          size="xl"
          class="mt-4"
          @click="checkout"
        >
          Continue Purchase
        </ui-button>

        <div
          class="block py-3 mt-2 w-full text-center text-gray-100 opacity-75 cursor-pointer"
          @click="() => {removeAppliedCoupon(); showPurchaseOverview = false;}"
        >
          Back
        </div>
      </div>

      <div v-else>
        <div
          v-if="checkoutStatus !== 'COMPLETE'"
          class="p-3 my-8"
        >
          <div class="mb-4">
            <div class="mb-1 text-sm font-medium dark:text-gray-100">
              Select your package
            </div>
            <p class="text-xs dark:text-gray-400">
              There is no difference in the feature set of the packages.
            </p>
          </div>
          <div class="space-y-3">
            <div
              v-for="item in plans"
              :key="item.id"
              :class="
                selectedPlanId === item.id
                  ? 'cursor-default ring ring-indigo-500 ring-offset-4 ring-offset-black bg-gray-800 text-white opacity-100'
                  : 'bg-gray-900 opacity-90'
              "
              class="
                flex
                justify-between
                items-center
                py-4
                px-4
                text-gray-100
                rounded-lg
                hover:opacity-100
                cursor-pointer
              "
              @click="selectedPlanId = item.id"
            >
              <div class="font-medium">
                {{ item.displayName }}
              </div>
              <div class="font-mono">
                {{ formatCurrency(item.price) }}
              </div>
            </div>
            <a
              target="_blank"
              href="https://opensea.io/collection/compass-lifetime-pass-1"
              class="
                flex
                justify-between
                items-center
                py-4
                px-4
                text-gray-100
                rounded-lg
                cursor-pointer
                bg-gray-900 opacity-100
              "
            >
              <div class="font-medium">Lifetime Pass</div>
              <div>
                <ui-button size="xs">Buy on OpenSea</ui-button>
              </div>
            </a>
          </div>
        </div>
        <div
          v-if="checkoutStatus !== 'COMPLETE'"
          class="mb-6"
        >
          <ui-button
            variant="primary"
            block
            size="xl"
            :disabled="selectedPlanId === null || isCheckingOut"
            @click="() => checkout('showPurchaseOverview')"
          >
            <span
              v-if="!isCheckingOut && selectedPlanId"
            >Buy Premium Access</span>
            <span v-else-if="!isCheckingOut">Select a Plan Above</span>
            <span v-else-if="isCheckingOut">{{ checkoutText }}</span>
          </ui-button>
          <div
            v-if="error"
            class="
              p-3
              mt-2
              text-center
              text-red-600
              rounded-lg
              bg-red-600 bg-opacity-20
              ring-red-600 ring-offset-0
            "
          >
            {{ error.message }}
          </div>
          <a
            href="/"
            class="block py-3 mt-2 w-full text-center text-gray-100 opacity-75"
          >Learn More</a>
        </div>
        <div v-if="checkoutStatus === 'COMPLETE'">
          <div class="my-5 text-center text-white">
            <div class="mb-4">
              <div class="text-xl font-medium">
                All set, welcome!
              </div>
            </div>
            <ui-button
              class="mx-auto"
              variant="primary"
              size="lg"
              :is-loading="isReloading"
              @click="reload"
            >
              Get Started With Premium
            </ui-button>
          </div>
        </div>
      </div>
    </div>
  </ui-modal>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  inject,
} from "vue";
import { useQuery, useMutation, ApolloClients } from "@vue/apollo-composable";
import createCheckoutMutation from "@/graphql/checkout/mutations/createCheckout.mutation.gql";
import premiumPlansQuery from "@/graphql/premium-plans/queries/premiumPlans.query.gql";
import billingInfoQuery from "@/graphql/billing-info/queries/billingInfo.query.gql";
import checkoutQuery from "@/graphql/checkout/queries/checkout.query.gql";
import checkCouponQuery from "@/graphql/checkout/queries/checkCoupon.query.gql";
import { event } from "vue-gtag";
import { formatCurrency } from "@/utils/filters";

import UiButton from "./ui/UiButton.vue";
import UiModal from "./ui/UiModal.vue";
import UiRadioCards from "./ui/UiRadioCards.vue";
import UiInput from "./ui/UiInput.vue"
import Web3 from "web3";
import { useUiDialog } from "./ui-dialog/useUiDialog";
import BillingInfoForm from "./BillingInfoForm.vue";
import ViewLoader from "./ViewLoader.vue";
import UiCard from "./ui/UiCard.vue";
import useBillingDataGraphql from '@/composition/billing/useBillingDataGraphql';
export default defineComponent({
  components: { UiModal, UiButton, UiRadioCards, UiInput, BillingInfoForm, ViewLoader, UiCard },
  inject: [ApolloClients],
  setup() {
    const checkoutId = ref(null);
    const isCheckingOut = ref(false);

    const dialog = useUiDialog();
    const {default: apollo} = inject(ApolloClients);
    const checkCoupon = (variables, options = {}) => {
      return apollo.query({
        query: checkCouponQuery,
        variables,
        ...options,
      });
    }

    const {
      result: billingInfoResult
    } = useQuery(billingInfoQuery);
    const billingInfo = computed(() => billingInfoResult.value?.billingInfo)

    const {
      result: premiumPlansResult,
    } = useQuery(premiumPlansQuery);
    const premiumPlans = computed(() => premiumPlansResult.value?.premiumPlans);

    const {
      result: checkoutResult,
      onResult,
    } = useQuery(
      checkoutQuery,
      () => ({ id: checkoutId.value }),
      () => ({ enabled: checkoutId.value !== null, pollInterval: 5000 })
    );
    const checkoutModel = computed(() => checkoutResult.value?.checkout);

    onResult((queryResult) => {
      if (queryResult.data.checkout.status === "COMPLETE") {
        isCheckingOut.value = false;
        event("checkout complete", {
          event_category: "premium lock",
          value: queryResult.data.checkout.value,
        });
      }
    });

    const { mutate: createCheckout } = useMutation(createCheckoutMutation)

    const {createBillingInfo} = useBillingDataGraphql();

    return {
      dialog,
      billingInfo,
      premiumPlans,
      isCheckingOut,
      checkoutModel,
      formatCurrency,
      createCheckout,
      createBillingInfo,
      checkCoupon,
    };
  },

  data() {
    return {
      isVisible: false,
      selectedPlanId: null,
      checkoutText: "",
      error: null,
      checkoutId: null,
      isReloading: false,
      showBillingInfoForm: false,
      billingInfoModel: {
        name: '',
        address: '',
        country: '',
        vatNumber: '',
      },
      billingInfoModelValid: true,
      fillingBillingInfo: false,
      showPurchaseOverview: false,
      couponCode: '',
      appliedCoupon: '',
      appliedCouponDiscount: 0,
      applyCouponLoading: false,
    };
  },

  computed: {
    plans() {
      return this.premiumPlans ?? [];
    },
    currentPrice() {
      return (
        this.plans.filter((item) => item.id === this.selectedPlanId)[0]
          ?.price ?? null
      );
    },
    checkoutStatus() {
      return this.checkoutModel?.status;
    },

    selectedPlanPrice() {
      return this.plans.find(item => item.id === this.selectedPlanId).price;
    }
  },

  methods: {
    reload() {
      this.isReloading = true;
      window.location.reload();
    },

    show(planId = null) {
      this.isVisible = true;
      this.selectedPlanId = planId;
      this.showBillingInfoForm = false;
      this.showPurchaseOverview = false;
    },

    planPriceWithDiscount(price) {
      return this.appliedCouponDiscount ? price - this.appliedCouponDiscount * price : price;
    },

    removeAppliedCoupon() {
      this.couponCode = '';
      this.appliedCoupon = '';
      this.appliedCouponDiscount = 0;
    },

    async applyCoupon() {
      if (!this.couponCode || this.appliedCoupon || !this.selectedPlanId) return;

      this.applyCouponLoading = true;

      let err;

      const result = await this.checkCoupon({
        couponCode: this.couponCode,
        planId: this.selectedPlanId,
      }, {
        fetchPolicy: 'network-only'
      }).catch(e => err = e);

      if (err) {
        this.dialog.info('Something went wrong!', { icon: 'far fa-exclamation-triangle text-lg text-red-600' });
        this.applyCouponLoading = false;
        return;
      }

      if (result.errors && Array.isArray(result.errors)) {
        result.errors.forEach(error => {
          this.dialog.info(error.message, { icon: 'far fa-exclamation-triangle text-lg text-red-600' });
        });
        this.applyCouponLoading = false;
        return;
      }

      this.appliedCoupon = this.couponCode;
      this.appliedCouponDiscount = result.data?.checkCoupon?.discountValue;
      this.applyCouponLoading = false;
    },

    async fillBillingInfo() {
      this.fillingBillingInfo = true;
      let errResponse;

      const result = await this.createBillingInfo({
        ...this.billingInfoModel
      }).catch(e => errResponse = e);

      if (errResponse) {
        this.dialog.info('Something went wrong!', { icon: 'far fa-exclamation-triangle text-lg text-red-600' });
        this.fillingBillingInfo = false;
        return;
      }

      if (result.errors && Array.isArray(result.errors)) {
        result.errors.forEach(error => {
          this.dialog.info(error.message, { icon: 'far fa-exclamation-triangle text-lg text-red-600' });
        });
        this.fillingBillingInfo = false;
        return;
      }

      this.checkout('showPurchaseOverview');
    },

    async checkout(step = '') {
      if (!this.billingInfo || !this.billingInfo.id) {
        this.showBillingInfoForm = true;
        return;
      }

      this.showPurchaseOverview = !!step && step === 'showPurchaseOverview';

      if (this.showPurchaseOverview) {
        this.removeAppliedCoupon();
        this.showBillingInfoForm = false;
        return;
      }

      this.showBillingInfoForm = false;
      this.isCheckingOut = true;
      this.checkoutText = "Preparing Transaction";

      const { data, errors } = await this.createCheckout({ planId: this.selectedPlanId, couponCode: this.appliedCoupon });

      const checkout = data?.checkout;

      if (errors && Array.isArray(errors)) {
        errors.forEach(error => {
          this.dialog.info(error.message, { icon: 'far fa-exclamation-triangle text-lg text-red-600' });
        });
        this.isCheckingOut = false;
        return;
      }

      this.$gtag.event("checkout start", {
        event_category: "premium lock",
        value: checkout.value,
      });

      const provider = await this.$store.dispatch('auth/getProvider');

      const web3 = new Web3(provider);

      let tx = web3.eth
        .sendTransaction({
          from: await web3.eth.getAccounts().then((res) => res[0]),
          to: checkout.paymentAddress,
          value: checkout.value * 10 ** 18,
        })
        .on("sending", () => {
          this.checkoutText = "Waiting For Wallet";
        })
        .on("sent", () => {
          this.checkoutText = "Waiting For Signature";
        })
        .on("transactionHash", (data) => {
          this.checkoutText = "Waiting For Transaction";
          this.transactionHash = data;
        })
        .on("error", (e) => {
          this.isCheckingOut = false;
          switch (e.code) {
          case 4001:
            this.error = new Error("You cancelled the transaction");
            this.$gtag.event("checkout cancel", {
              event_category: "premium lock",
            });
            break;
          default:
            this.error = e;
          }
        });

      await tx;

      this.checkoutText = "Waiting For Confirmation";
      this.checkoutId = checkout.id;
    },
  },
});
</script>

<style>
.bg-noise {
  background: url("../assets/bg-noise.png") black;
}
</style>